import { openPendingDocumentTab } from "@/lib/open-document-tab";
import { resolveStoredFileUrl } from "@/lib/storage-url";
import { TEMPLATE_BUCKET } from "@/lib/tenant-house-documents";

/** Bucket that backs every tenant_documents.file_url stored as a bare path. */
const TENANT_DOCS_BUCKET = "tenant-documents";

/**
 * Buckets a `tenant_documents.file_url` may name explicitly, as
 * "<bucket>/<path>".
 *
 * The deposit refund statement and handover protocols live in private
 * buckets, not the public tenant-documents one. An allowlist rather than
 * "split on the first slash": legacy paths like
 * "wohnungsgeberbestaetigung/<id>.pdf" have a leading segment that is a
 * folder, not a bucket, and must keep resolving as they always have.
 */
export const QUALIFIED_DOCUMENT_BUCKETS = [
  "deposit-settlements",
  "handover-protocols",
] as const;

/**
 * `tenant_documents.file_url` may hold a full Storage URL (the public URL
 * written at upload time, pointing into `tenant-documents` or `contracts`),
 * a bucket-qualified path ("handover-protocols/<protocolId>/…"), or a bare
 * Storage path in the tenant-documents bucket (e.g.
 * "wohnungsgeberbestaetigung/<bookingId>.pdf"). All three are signed: a path
 * used as an `<a href>` resolves against the SPA route and 404s, and a stored
 * public URL stops working once the bucket goes private (COL-436). Returns a
 * browser-openable URL, or null on failure.
 */
export async function resolveTenantDocumentUrl(
  fileUrl: string,
  opts?: { download?: string | boolean }
): Promise<string | null> {
  for (const known of QUALIFIED_DOCUMENT_BUCKETS) {
    if (fileUrl.startsWith(`${known}/`)) {
      return resolveStoredFileUrl(fileUrl.slice(known.length + 1), known, opts);
    }
  }
  return resolveStoredFileUrl(fileUrl, TENANT_DOCS_BUCKET, opts);
}

/**
 * Open a `tenant_documents.file_url` in a new tab. The tab opens inside the
 * click (popup blockers) and is pointed at the signed URL once it resolves.
 * Resolves false when the document can't be opened, so the caller can say so
 * in its own words.
 */
export async function openTenantDocument(
  fileUrl: string,
  opts?: { download?: string | boolean }
): Promise<boolean> {
  const tab = openPendingDocumentTab();
  try {
    const url = await resolveTenantDocumentUrl(fileUrl, opts);
    if (url) {
      tab.navigate(url);
      return true;
    }
  } catch {
    // fall through: same outcome as a failed signature
  }
  tab.cancel();
  return false;
}

/**
 * Open one of the static move-in info PDFs (COL-533).
 *
 * Same contract as `openTenantDocument`: the tab opens inside the click so
 * popup blockers allow it, and is pointed at the signed URL once it
 * resolves. Returns false when the object is missing or unreadable — the
 * move-in email treats a missing one as non-fatal too, and a project that
 * was never seeded is the ordinary way to hit this — so the caller can say
 * so rather than leaving a blank tab open.
 */
export async function openHouseDocument(path: string): Promise<boolean> {
  const tab = openPendingDocumentTab();
  try {
    const url = await resolveStoredFileUrl(path, TEMPLATE_BUCKET);
    if (url) {
      tab.navigate(url);
      return true;
    }
  } catch {
    // fall through: same outcome as a failed signature
  }
  tab.cancel();
  return false;
}
